import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Session, SupabaseClient, User } from "@supabase/supabase-js";
import {
  describeSupabaseInitError,
  getSupabaseClient,
} from "@/integrations/supabase/browser-client";

interface AuthState {
  supabase: SupabaseClient | null;
  user: User | null;
  session: Session | null;
  loading: boolean;
}

const AuthContext = createContext<AuthState>({
  supabase: null,
  user: null,
  session: null,
  loading: true,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [supabase, setSupabase] = useState<SupabaseClient | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let unsub: (() => void) | undefined;

    getSupabaseClient()
      .then(async (client) => {
        if (!mounted) return;
        setSupabase(client);
        let data: { session: Session | null };
        try {
          ({ data } = await client.auth.getSession());
        } catch (err) {
          console.error(
            "[DealDraft] Supabase init failed:",
            describeSupabaseInitError("auth.getSession", err),
          );
          throw err;
        }
        if (!mounted) return;
        setSession(data.session);
        setLoading(false);
        const sub = client.auth.onAuthStateChange((_event, s) => setSession(s));
        unsub = () => sub.data.subscription.unsubscribe();
      })
      .catch((err) => {
        // getPublicConfig failures are logged in browser-client.ts
        const message = err instanceof Error ? err.message : String(err);
        if (message !== "Forbidden") {
          console.error(
            "[DealDraft] Supabase init failed:",
            describeSupabaseInitError("authProvider", err),
          );
        }
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
      unsub?.();
    };
  }, []);

  const value = useMemo(
    () => ({ supabase, user: session?.user ?? null, session, loading }),
    [supabase, session, loading],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
